/**
 * DocumentoGrid — grade de cards de documento que abre o visualizador.
 *
 * É o visual aprovado da aba Documentos do Cliente 360 (.doc-grid/.doc-card),
 * agora em styled-components para valer fora do `.cli360` — o cockpit do lead
 * passou a usar o MESMO desenho e o MESMO visualizador (CRM-50), que era o
 * pedido do dono. Antes o anexo do lead era um link sublinhado para a URL
 * assinada do S3 e o card do cliente era exclusivo do 360.
 *
 * Props:
 *   - documentos: {id, nome, url, mime?, tamanho?, data?, fonte?, autor?,
 *                  situacao?, situacao_tom?, tipo?}[]
 *   - visualPorTipo?: mapa `tipo -> {icon, bg, cor}` (a aba do cliente passa o
 *     dela); sem ele o ícone sai do tipo de arquivo
 *   - onExcluir?(doc) => Promise — com ele cada card ganha "Apagar" (confirma
 *     antes, no diálogo do design system, não no window.confirm)
 *   - carregando?, erro?, vazio?: ReactNode, minColuna? (padrão 230)
 */
import React, { useState } from "react";
import {
  FiAlertCircle,
  FiFile,
  FiFileText,
  FiGrid,
  FiImage,
  FiTrash2,
} from "react-icons/fi";
import styled from "styled-components";

import { formatarDataLocal } from "../../utils/dinheiro";
import { detectarKind, formatarTamanho } from "../../utils/documentos";
import { EmptyMsg, ErrorMsg, Pill } from "../../pages/Orcamentos/styles";
import ConfirmDialog from "../ConfirmDialog";
import DocumentViewerModal from "../DocumentViewerModal";

// Ícone por tipo de ARQUIVO. Cores categóricas (accent + paleta de charts),
// nunca a paleta funcional: info/warning/danger comunicam ESTADO, e o tipo de
// um arquivo não é estado.
const VISUAL_POR_KIND = {
  pdf: { Icone: FiFileText, bg: "var(--accent-soft)", cor: "var(--accent)" },
  docx: { Icone: FiFileText, bg: "var(--accent-soft)", cor: "var(--accent)" },
  html: { Icone: FiFileText, bg: "var(--accent-soft)", cor: "var(--accent)" },
  text: { Icone: FiFileText, bg: "var(--accent-soft)", cor: "var(--accent)" },
  image: { Icone: FiImage, bg: "rgba(52,211,153,.14)", cor: "var(--chart-2)" },
  xlsx: { Icone: FiGrid, bg: "rgba(192,132,252,.14)", cor: "var(--chart-6)" },
  csv: { Icone: FiGrid, bg: "rgba(192,132,252,.14)", cor: "var(--chart-6)" },
  unsupported: { Icone: FiFile, bg: "var(--neutral-bg)", cor: "var(--neutral-text)" },
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${({ $min }) => $min}px, 1fr));
  gap: 14px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 15px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: var(--radius-lg, 12px);
  background: ${({ theme }) => theme.surface};
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }
`;

const Abrir = styled.button`
  appearance: none;
  display: flex;
  gap: 13px;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: ${({ $clicavel }) => ($clicavel ? "pointer" : "default")};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 3px;
    border-radius: 8px;
  }
`;

const Icone = styled.span`
  width: 42px;
  height: 42px;
  border-radius: var(--radius-md, 10px);
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;

const Info = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Nome = styled.span`
  font-weight: 600;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textTertiary};
`;

const Acoes = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const BotaoApagar = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 7px;
  border: 1px solid ${({ theme }) => theme.border};
  background: transparent;
  color: ${({ theme }) => theme.dangerText};
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.dangerSurface};
    border-color: ${({ theme }) => theme.dangerBorder};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

function visualDoDocumento(doc, visualPorTipo) {
  if (visualPorTipo && doc.tipo) {
    const escolhido = visualPorTipo[doc.tipo] || visualPorTipo.OUTROS;
    if (escolhido) return escolhido;
  }
  return VISUAL_POR_KIND[detectarKind(doc)] || VISUAL_POR_KIND.unsupported;
}

function linhaMeta(doc) {
  return [
    doc.fonte,
    doc.autor ? `por ${doc.autor}` : null,
    doc.tamanho ? formatarTamanho(doc.tamanho) : null,
    // ADR-0059: `new Date("2026-09-10")` é meia-noite UTC e vira o dia anterior.
    doc.data ? formatarDataLocal(doc.data) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export default function DocumentoGrid({
  documentos = [],
  visualPorTipo = null,
  onExcluir = null,
  carregando = false,
  erro = null,
  vazio = null,
  minColuna = 230,
}) {
  const [docAberto, setDocAberto] = useState(null);
  const [alvoExclusao, setAlvoExclusao] = useState(null);
  const [erroExclusao, setErroExclusao] = useState(null);

  const confirmarExclusao = async () => {
    try {
      await onExcluir(alvoExclusao);
      setAlvoExclusao(null);
      setErroExclusao(null);
    } catch (err) {
      // O diálogo continua aberto para a pessoa tentar de novo; a mensagem do
      // backend aparece aqui em vez de sumir atrás do modal.
      setErroExclusao(err?.message || "Não foi possível apagar o arquivo.");
    }
  };

  if (carregando) {
    return <EmptyMsg style={{ padding: 8 }}>Carregando documentos…</EmptyMsg>;
  }

  return (
    <>
      {erro ? (
        <ErrorMsg>
          <FiAlertCircle size={12} aria-hidden="true" /> {erro}
        </ErrorMsg>
      ) : null}

      {documentos.length === 0 && !erro ? (
        vazio || <EmptyMsg style={{ padding: 8 }}>Nenhum documento.</EmptyMsg>
      ) : (
        <Grid $min={minColuna}>
          {documentos.map((doc) => {
            const { Icone: IconeArquivo, icon, bg, cor } = visualDoDocumento(doc, visualPorTipo);
            const clicavel = Boolean(doc.url);
            return (
              <Card key={doc.id}>
                <Abrir
                  type="button"
                  $clicavel={clicavel}
                  onClick={clicavel ? () => setDocAberto(doc) : undefined}
                  disabled={!clicavel}
                  aria-label={clicavel ? `Abrir documento ${doc.nome}` : undefined}
                  title={clicavel ? `Abrir ${doc.nome}` : doc.nome}
                >
                  <Icone style={{ background: bg, color: cor }}>
                    {icon || (IconeArquivo ? <IconeArquivo size={20} aria-hidden="true" /> : null)}
                  </Icone>
                  <Info>
                    <Nome>{doc.nome}</Nome>
                    <Meta>{linhaMeta(doc)}</Meta>
                    {doc.situacao ? (
                      <span style={{ marginTop: 5 }}>
                        <Pill $tone={doc.situacao_tom || "neutral"}>{doc.situacao}</Pill>
                      </span>
                    ) : null}
                  </Info>
                </Abrir>
                {onExcluir ? (
                  <Acoes>
                    <BotaoApagar
                      type="button"
                      onClick={() => {
                        setErroExclusao(null);
                        setAlvoExclusao(doc);
                      }}
                      aria-label={`Apagar ${doc.nome}`}
                    >
                      <FiTrash2 size={11} aria-hidden="true" /> Apagar
                    </BotaoApagar>
                  </Acoes>
                ) : null}
              </Card>
            );
          })}
        </Grid>
      )}

      <DocumentViewerModal doc={docAberto} onClose={() => setDocAberto(null)} />

      <ConfirmDialog
        open={Boolean(alvoExclusao)}
        titulo={`Apagar "${alvoExclusao?.nome}"?`}
        descricao={
          erroExclusao || "O arquivo sai da lista e do armazenamento. Não dá para desfazer."
        }
        confirmarLabel="Sim, apagar"
        cancelarLabel="Não"
        onConfirmar={confirmarExclusao}
        onCancelar={() => {
          setAlvoExclusao(null);
          setErroExclusao(null);
        }}
      />
    </>
  );
}
